import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowDown, ArrowUp, BookOpen, Eye, FileText, Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import HelpMarkdown from "components/support/HelpMarkdown";
import {
    AdminHelpArticle,
    AdminHelpCollection,
    createHelpArticle,
    createHelpCollection,
    deleteHelpArticle,
    deleteHelpCollection,
    getAdminHelpArticle,
    getAdminHelpArticles,
    getAdminHelpCollections,
    supportErrorMessage,
    updateHelpArticle,
    updateHelpCollection,
} from "interfaces/support";

/**
 * /bo/help-center — the help center shown in the « Aide » tab of the support widget. « Utilisateurs » menu.
 * Collections on the left, their articles in the middle, the article being written on the right
 * (Markdown, with a preview rendered exactly as in the widget). Only published collections and
 * published articles reach the widget; an article is created as a draft.
 */

const inputClass = "w-full rounded-lg border border-[#e6e9f0] px-3 py-2 text-sm text-[#17244a] focus:border-[#17244a] focus:outline-none";
const MARKDOWN_HELP = "## Titre · **gras** · *italique* · - liste · 1. liste · > encadré · [lien](https://…)";

function PublishedBadge({ published }: { published: boolean }) {
    return published ? (
        <span className="shrink-0 rounded-full border border-green-200 bg-green-50 px-2 py-0.5 text-[11px] font-medium text-green-700">Publié</span>
    ) : (
        <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600">Brouillon</span>
    );
}

function ArticleEditor({
    id,
    collections,
    onSaved,
    onDeleted,
}: {
    id: string;
    collections: AdminHelpCollection[];
    onSaved: (a: AdminHelpArticle) => void;
    onDeleted: () => void;
}) {
    const [article, setArticle] = useState<AdminHelpArticle | null>(null);
    const [title, setTitle] = useState("");
    const [body, setBody] = useState("");
    const [collectionId, setCollectionId] = useState("");
    const [published, setPublished] = useState(false);
    const [preview, setPreview] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [savedAt, setSavedAt] = useState<Date | null>(null);

    useEffect(() => {
        setArticle(null);
        setSavedAt(null);
        getAdminHelpArticle(id)
            .then((a) => {
                setArticle(a);
                setTitle(a.title);
                setBody(a.body ?? "");
                setCollectionId(a.collectionId);
                setPublished(a.published);
            })
            .catch((e) => setError(supportErrorMessage(e)));
    }, [id]);

    const dirty = !!article && (title !== article.title || body !== (article.body ?? "") || collectionId !== article.collectionId || published !== article.published);

    const save = async () => {
        setSaving(true);
        setError(null);
        try {
            const saved = await updateHelpArticle(id, { title, body, collectionId, published });
            setArticle(saved);
            setSavedAt(new Date());
            onSaved(saved);
        } catch (e) {
            setError(supportErrorMessage(e));
        }
        setSaving(false);
    };

    const remove = async () => {
        if (!window.confirm(`Supprimer l'article « ${article?.title} » ?`)) return;
        try {
            await deleteHelpArticle(id);
            onDeleted();
        } catch (e) {
            setError(supportErrorMessage(e));
        }
    };

    if (!article) {
        return <div className="flex flex-1 items-center justify-center text-sm text-[#68738d]">{error ?? <Loader2 className="h-5 w-5 animate-spin" />}</div>;
    }

    return (
        <div className="flex min-w-0 flex-1 flex-col">
            <div className="flex flex-wrap items-center gap-2 border-b border-[#e6e9f0] px-5 py-3">
                <div className="flex rounded-lg bg-[#f5f7fb] p-0.5">
                    {[false, true].map((p) => (
                        <button
                            key={String(p)}
                            type="button"
                            onClick={() => setPreview(p)}
                            className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium ${preview === p ? "bg-white text-[#17244a] shadow-sm" : "text-[#68738d]"}`}
                        >
                            {p ? <Eye className="h-3.5 w-3.5" /> : <Pencil className="h-3.5 w-3.5" />}
                            {p ? "Aperçu" : "Écrire"}
                        </button>
                    ))}
                </div>
                <label className="ml-2 flex items-center gap-2 text-xs text-[#273354]">
                    <input type="checkbox" checked={published} onChange={(e) => setPublished(e.target.checked)} />
                    Publié dans le widget
                </label>
                <span className="ml-auto text-[11px] text-[#7a849b]">
                    {article.views} vue{article.views > 1 ? "s" : ""}
                    {article.updatedBy ? ` · modifié par ${article.updatedBy}` : ""}
                </span>
                <button type="button" onClick={remove} title="Supprimer" className="rounded-lg p-1.5 text-[#7a849b] hover:bg-red-50 hover:text-red-600">
                    <Trash2 className="h-4 w-4" />
                </button>
                <button
                    type="button"
                    onClick={save}
                    disabled={!dirty || saving || !title.trim()}
                    className="rounded-lg bg-[#1A1A1A] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#333] disabled:opacity-50"
                >
                    {saving ? "Enregistrement…" : dirty ? "Enregistrer" : savedAt ? "Enregistré" : "Enregistrer"}
                </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4">
                {error && <p className="mb-3 text-xs text-red-600">{error}</p>}
                {preview ? (
                    <div className="mx-auto max-w-md rounded-2xl border border-[#e6e9f0] p-6">
                        <h2 className="mb-6 text-2xl font-semibold leading-tight text-ink">{title}</h2>
                        <HelpMarkdown source={body} />
                    </div>
                ) : (
                    <div className="space-y-3">
                        <div className="grid grid-cols-3 gap-3">
                            <div className="col-span-2">
                                <p className="mb-1 text-xs text-[#68738d]">Titre</p>
                                <input value={title} onChange={(e) => setTitle(e.target.value)} className={inputClass} />
                            </div>
                            <div>
                                <p className="mb-1 text-xs text-[#68738d]">Collection</p>
                                <select value={collectionId} onChange={(e) => setCollectionId(e.target.value)} className={inputClass}>
                                    {collections.map((c) => (
                                        <option key={c._id} value={c._id}>
                                            {c.title}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div>
                            <p className="mb-1 text-xs text-[#68738d]">Contenu (Markdown) — {MARKDOWN_HELP}</p>
                            <textarea
                                value={body}
                                onChange={(e) => setBody(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "s" && (e.metaKey || e.ctrlKey)) {
                                        e.preventDefault();
                                        if (dirty) save();
                                    }
                                }}
                                rows={22}
                                className={`${inputClass} font-mono leading-relaxed`}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default function HelpCenter() {
    const [searchParams, setSearchParams] = useSearchParams();
    const collectionId = searchParams.get("collection");
    const articleId = searchParams.get("article");
    const [collections, setCollections] = useState<AdminHelpCollection[] | null>(null);
    const [articles, setArticles] = useState<AdminHelpArticle[] | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [newCollection, setNewCollection] = useState("");
    const [editingCollection, setEditingCollection] = useState<{ title: string; description: string } | null>(null);

    const setParams = (next: { collection?: string | null; article?: string | null }) =>
        setSearchParams((p) => {
            for (const [k, v] of Object.entries(next)) {
                if (v) p.set(k, v);
                else p.delete(k);
            }
            return p;
        });

    const loadCollections = useCallback(async () => {
        try {
            const list = await getAdminHelpCollections();
            setCollections(list);
            return list;
        } catch (e) {
            setError(supportErrorMessage(e));
            return [];
        }
    }, []);

    const loadArticles = useCallback(async () => {
        if (!collectionId) return setArticles(null);
        try {
            setArticles(await getAdminHelpArticles(collectionId));
        } catch (e) {
            setError(supportErrorMessage(e));
        }
    }, [collectionId]);

    useEffect(() => {
        loadCollections().then((list) => {
            if (!collectionId && list[0]) setParams({ collection: list[0]._id });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setEditingCollection(null);
        loadArticles();
    }, [loadArticles]);

    const current = collections?.find((c) => c._id === collectionId) ?? null;

    const addCollection = async () => {
        if (!newCollection.trim()) return;
        setError(null);
        try {
            const created = await createHelpCollection({ title: newCollection.trim() });
            setNewCollection("");
            await loadCollections();
            setParams({ collection: created._id, article: null });
        } catch (e) {
            setError(supportErrorMessage(e));
        }
    };

    const patchCollection = async (id: string, fields: Parameters<typeof updateHelpCollection>[1]) => {
        setError(null);
        try {
            await updateHelpCollection(id, fields);
            await loadCollections();
        } catch (e) {
            setError(supportErrorMessage(e));
        }
    };

    // Swap with the neighbour; orders are renumbered so equal values can't stall a move
    const moveCollection = async (index: number, delta: -1 | 1) => {
        if (!collections) return;
        const target = index + delta;
        if (target < 0 || target >= collections.length) return;
        const reordered = [...collections];
        [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
        setCollections(reordered);
        await Promise.all(reordered.map((c, i) => (c.order !== i + 1 ? updateHelpCollection(c._id, { order: i + 1 }) : null)));
        loadCollections();
    };

    const moveArticle = async (index: number, delta: -1 | 1) => {
        if (!articles) return;
        const target = index + delta;
        if (target < 0 || target >= articles.length) return;
        const reordered = [...articles];
        [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
        setArticles(reordered);
        await Promise.all(reordered.map((a, i) => (a.order !== i + 1 ? updateHelpArticle(a._id, { order: i + 1 }) : null)));
        loadArticles();
    };

    const removeCollection = async (c: AdminHelpCollection) => {
        if (!window.confirm(`Supprimer la collection « ${c.title} » ?`)) return;
        setError(null);
        try {
            await deleteHelpCollection(c._id);
            const list = await loadCollections();
            setParams({ collection: list[0]?._id ?? null, article: null });
        } catch (e) {
            setError(supportErrorMessage(e));
        }
    };

    const addArticle = async () => {
        if (!collectionId) return;
        setError(null);
        try {
            const created = await createHelpArticle({ collectionId, title: "Nouvel article", body: "" });
            await Promise.all([loadArticles(), loadCollections()]);
            setParams({ article: created._id });
        } catch (e) {
            setError(supportErrorMessage(e));
        }
    };

    return (
        <div className="flex h-[calc(100vh-69px)] flex-col bg-[#f5f7fb] px-5 py-6 md:px-8">
            <header className="mb-5">
                <h1 className="text-2xl font-semibold text-[#17244a]">Centre d'aide</h1>
                <p className="mt-0.5 text-sm text-[#68738d]">
                    Les articles de l'onglet « Aide » du chat support. Seuls les collections et articles publiés y apparaissent ; un nouvel article est un brouillon.
                </p>
                {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
            </header>

            <div className="flex min-h-0 flex-1 overflow-hidden rounded-2xl border border-[#e3e8f1] bg-white">
                {/* Collections */}
                <div className="flex w-64 shrink-0 flex-col border-r border-[#e6e9f0]">
                    <p className="border-b border-[#e6e9f0] px-4 py-3 text-xs font-semibold uppercase tracking-wide text-[#68738d]">Collections</p>
                    <div className="flex-1 overflow-y-auto p-2">
                        {!collections && <Loader2 className="mx-auto my-6 h-5 w-5 animate-spin text-[#7a849b]" />}
                        {collections?.map((c, i) => (
                            <div
                                key={c._id}
                                className={`group flex items-center gap-1 rounded-lg px-2 py-2 ${collectionId === c._id ? "bg-[#f5f7fb]" : "hover:bg-[#f9fafc]"}`}
                            >
                                <button type="button" onClick={() => setParams({ collection: c._id, article: null })} className="min-w-0 flex-1 text-left">
                                    <p className={`truncate text-sm ${collectionId === c._id ? "font-semibold text-[#17244a]" : "text-[#273354]"}`}>{c.title}</p>
                                    <p className="text-[11px] text-[#7a849b]">
                                        {c.publishedCount}/{c.articleCount} publié{c.publishedCount > 1 ? "s" : ""}
                                        {!c.published && " · masquée"}
                                    </p>
                                </button>
                                <div className="flex flex-col opacity-0 group-hover:opacity-100">
                                    <button type="button" onClick={() => moveCollection(i, -1)} aria-label="Monter" className="text-[#7a849b] hover:text-[#17244a]">
                                        <ArrowUp className="h-3.5 w-3.5" />
                                    </button>
                                    <button type="button" onClick={() => moveCollection(i, 1)} aria-label="Descendre" className="text-[#7a849b] hover:text-[#17244a]">
                                        <ArrowDown className="h-3.5 w-3.5" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="flex gap-2 border-t border-[#e6e9f0] p-3">
                        <input
                            value={newCollection}
                            onChange={(e) => setNewCollection(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && addCollection()}
                            placeholder="Nouvelle collection"
                            className={inputClass}
                        />
                        <button
                            type="button"
                            onClick={addCollection}
                            disabled={!newCollection.trim()}
                            aria-label="Créer la collection"
                            className="rounded-lg bg-[#1A1A1A] px-2.5 text-white hover:bg-[#333] disabled:opacity-50"
                        >
                            <Plus className="h-4 w-4" />
                        </button>
                    </div>
                </div>

                {/* Articles of the collection */}
                <div className="flex w-80 shrink-0 flex-col border-r border-[#e6e9f0]">
                    {current ? (
                        <>
                            <div className="space-y-2 border-b border-[#e6e9f0] px-4 py-3">
                                {editingCollection ? (
                                    <div className="space-y-2">
                                        <input
                                            value={editingCollection.title}
                                            onChange={(e) => setEditingCollection({ ...editingCollection, title: e.target.value })}
                                            className={inputClass}
                                        />
                                        <input
                                            value={editingCollection.description}
                                            onChange={(e) => setEditingCollection({ ...editingCollection, description: e.target.value })}
                                            placeholder="Description (facultatif)"
                                            className={inputClass}
                                        />
                                        <div className="flex gap-2">
                                            <button
                                                type="button"
                                                onClick={async () => {
                                                    await patchCollection(current._id, editingCollection);
                                                    setEditingCollection(null);
                                                }}
                                                className="rounded-lg bg-[#1A1A1A] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#333]"
                                            >
                                                Enregistrer
                                            </button>
                                            <button type="button" onClick={() => setEditingCollection(null)} className="rounded-lg px-3 py-1.5 text-xs text-[#273354] hover:bg-[#f5f7fb]">
                                                Annuler
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex items-start gap-2">
                                        <div className="min-w-0 flex-1">
                                            <p className="truncate text-sm font-semibold text-[#17244a]">{current.title}</p>
                                            {current.description && <p className="text-xs text-[#68738d]">{current.description}</p>}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setEditingCollection({ title: current.title, description: current.description ?? "" })}
                                            aria-label="Modifier la collection"
                                            className="rounded-lg p-1 text-[#7a849b] hover:bg-[#f5f7fb] hover:text-[#17244a]"
                                        >
                                            <Pencil className="h-3.5 w-3.5" />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => removeCollection(current)}
                                            disabled={current.articleCount > 0}
                                            title={current.articleCount > 0 ? "Videz la collection pour la supprimer" : "Supprimer la collection"}
                                            className="rounded-lg p-1 text-[#7a849b] hover:bg-red-50 hover:text-red-600 disabled:opacity-30 disabled:hover:bg-[transparent] disabled:hover:text-[#7a849b]"
                                        >
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </button>
                                    </div>
                                )}
                                <label className="flex items-center gap-2 text-xs text-[#273354]">
                                    <input type="checkbox" checked={current.published} onChange={(e) => patchCollection(current._id, { published: e.target.checked })} />
                                    Collection visible dans le widget
                                </label>
                            </div>

                            <div className="flex-1 overflow-y-auto p-2">
                                {!articles && <Loader2 className="mx-auto my-6 h-5 w-5 animate-spin text-[#7a849b]" />}
                                {articles?.length === 0 && <p className="p-4 text-center text-xs text-[#68738d]">Aucun article dans cette collection.</p>}
                                {articles?.map((a, i) => (
                                    <div
                                        key={a._id}
                                        className={`group flex items-center gap-2 rounded-lg px-2 py-2 ${articleId === a._id ? "bg-[#f5f7fb]" : "hover:bg-[#f9fafc]"}`}
                                    >
                                        <FileText className="h-4 w-4 shrink-0 text-[#7a849b]" />
                                        <button type="button" onClick={() => setParams({ article: a._id })} className="min-w-0 flex-1 text-left">
                                            <p className={`truncate text-sm ${articleId === a._id ? "font-semibold text-[#17244a]" : "text-[#273354]"}`}>{a.title}</p>
                                            <p className="text-[11px] text-[#7a849b]">
                                                {a.views} vue{a.views > 1 ? "s" : ""}
                                            </p>
                                        </button>
                                        <PublishedBadge published={a.published} />
                                        <div className="flex flex-col opacity-0 group-hover:opacity-100">
                                            <button type="button" onClick={() => moveArticle(i, -1)} aria-label="Monter" className="text-[#7a849b] hover:text-[#17244a]">
                                                <ArrowUp className="h-3.5 w-3.5" />
                                            </button>
                                            <button type="button" onClick={() => moveArticle(i, 1)} aria-label="Descendre" className="text-[#7a849b] hover:text-[#17244a]">
                                                <ArrowDown className="h-3.5 w-3.5" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="border-t border-[#e6e9f0] p-3">
                                <button
                                    type="button"
                                    onClick={addArticle}
                                    className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#1A1A1A] px-3 py-2 text-xs font-medium text-white hover:bg-[#333]"
                                >
                                    <Plus className="h-4 w-4" />
                                    Nouvel article
                                </button>
                            </div>
                        </>
                    ) : (
                        <div className="flex flex-1 flex-col items-center justify-center gap-2 p-6 text-center text-sm text-[#68738d]">
                            <BookOpen className="h-6 w-6 text-[#b3bacb]" />
                            Créez une première collection à gauche (ex. « Premiers pas »).
                        </div>
                    )}
                </div>

                {/* Editor */}
                {articleId && collections ? (
                    <ArticleEditor
                        key={articleId}
                        id={articleId}
                        collections={collections}
                        onSaved={(a) => {
                            loadCollections();
                            if (a.collectionId !== collectionId) setParams({ collection: a.collectionId });
                            else loadArticles();
                        }}
                        onDeleted={() => {
                            setParams({ article: null });
                            loadArticles();
                            loadCollections();
                        }}
                    />
                ) : (
                    <div className="flex flex-1 flex-col items-center justify-center gap-2 text-sm text-[#68738d]">
                        <FileText className="h-8 w-8 text-[#b3bacb]" />
                        Sélectionnez un article.
                    </div>
                )}
            </div>
        </div>
    );
}
